import { useState } from 'react';

type SubmitState = 'idle' | 'success' | 'error';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: '',
    message: ''
  });
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitState>('idle');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    // 必須チェック（最低限）
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      setSubmitStatus('error');
      return;
    }
    if (!agreePrivacy) return;
    setShowConfirm(true);
  };

  const submitToServer = async () => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams({
        ...formData,
        privacy: agreePrivacy ? '同意する' : ''
      }).toString();
      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          inquiry_type: '',
          message: ''
        });
        setAgreePrivacy(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-green-800 mb-4">
            <strong>お問い合わせ</strong>
          </h3>
          <div className="w-24 h-1 bg-yellow-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            商品に関するご質問、お見積もり、その他お気軽にお問い合わせください。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* ----- 左：会社情報（元のまま） ----- */}
          <div>
            <div className="bg-yellow-50 p-8 rounded-lg border-2 border-yellow-400 mb-8">
              <h4 className="text-2xl font-bold text-green-800 mb-6">
                <strong>会社情報</strong>
              </h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-building-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800">会社名</h5>
                    <p className="text-gray-700">株式会社MELM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-map-pin-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800">所在地</h5>
                    <p className="text-gray-700">
                      〒546-0043<br />
                      大阪府大阪市東住吉区駒川3-27-39<br />
                      プレジオールKOMAGAWA 204
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-user-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800">代表者</h5>
                    <p className="text-gray-700">宮下　亜希</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-phone-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800">電話番号</h5>
                    <p className="text-gray-700">080-2466-3126 / 050-8894-7919</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-time-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h5 className="font-semibold text-green-800">営業時間</h5>
                    <p className="text-gray-700">
                      平日 9:00〜18:00<br />
                      土曜 9:00〜17:00<br />
                      日祝 休業
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-800 text-white p-6 rounded-lg">
              <h4 className="text-xl font-bold mb-4">
                <strong>メールでのお問い合わせ</strong>
              </h4>
              <p className="mb-4">お問い合わせはメールでも受け付けております。</p>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                  <i className="ri-phone-line text-green-800 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm">お電話でのお問い合わせ</p>
                  <p className="text-lg font-bold">080-2466-3126 / 050-8894-7919</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                  <i className="ri-mail-line text-green-800 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm">お問い合わせ窓口</p>
                  <p className="text-lg font-bold break-all">accounting@melm-inc.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* ----- 右：フォーム ----- */}
          <div>
            <form
              onSubmit={openConfirm}
              className="bg-yellow-50 p-8 rounded-lg border-2 border-yellow-400"
            >
              <h4 className="text-2xl font-bold text-green-800 mb-6">
                <strong>お問い合わせフォーム</strong>
              </h4>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                  お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-green-800 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                    placeholder="山田太郎"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-green-800 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-green-800 mb-2">
                    電話番号
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-green-800 mb-2">
                    会社名・店舗名
                  </label>
                  <input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="inquiry_type" className="block text-sm font-semibold text-green-800 mb-2">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm pr-8 appearance-none"
                  >
                    <option value="">選択してください</option>
                    <option value="商品について">商品について</option>
                    <option value="見積もり依頼">見積もり依頼</option>
                    <option value="卸売について">卸売について</option>
                    <option value="通販について">通販について</option>
                    <option value="その他">その他</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-green-800 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 text-sm resize-vertical"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              {/* プライバシー同意 */}
              <div className="mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreePrivacy}
                    onChange={(e) => setAgreePrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    <a href="/privacy" className="text-green-800 underline font-semibold"  rel="noreferrer">
                      プライバシーポリシー
                    </a>
                    に同意します
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !agreePrivacy}
                className="w-full bg-green-800 text-white py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* 確認モーダル */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h5 className="text-xl font-bold text-green-800 mb-4">送信しますか？</h5>
            <div className="space-y-2 text-sm text-gray-700 mb-6">
              <p><span className="font-semibold">お名前：</span>{formData.name}</p>
              <p><span className="font-semibold">メール：</span>{formData.email}</p>
              {formData.phone && <p><span className="font-semibold">電話：</span>{formData.phone}</p>}
              {formData.company && <p><span className="font-semibold">会社：</span>{formData.company}</p>}
              <p><span className="font-semibold">種別：</span>{formData.inquiry_type || '-'}</p>
              <p className="whitespace-pre-wrap"><span className="font-semibold">内容：</span>{formData.message}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                戻る
              </button>
              <button
                onClick={submitToServer}
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg bg-green-800 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? '送信中…' : '送信する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
